(() => {
  const namespace = 'TrueVitalsOrganicPdp';
  if (window[namespace]) {
    window[namespace].initWithin(document);
    return;
  }

  const initHero = (root) => {
    if (!root || root.dataset.tvReady === 'true') return;
    root.dataset.tvReady = 'true';

    const buttons = [...root.querySelectorAll('[data-tv-media-button]')];
    const items = [...root.querySelectorAll('[data-tv-media]')];
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const target = button.getAttribute('data-tv-media-button');
        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        items.forEach((item) => {
          const active = item.getAttribute('data-tv-media') === target;
          item.hidden = !active;
          item.classList.toggle('is-active', active);
        });
      });
    });

    const sticky = root.querySelector('[data-tv-sticky]');
    const primaryForm = root.querySelector('.tv-pdp__form');
    const directForms = [...root.querySelectorAll('[data-tv-direct-checkout]')];
    const directButtons = directForms.flatMap((form) => [
      ...form.querySelectorAll('button[type="submit"]'),
    ]);
    const priorDisabledStates = new WeakMap();
    let checkoutPending = false;

    const setCheckoutPending = (pending) => {
      if (pending && !checkoutPending) {
        directButtons.forEach((button) => priorDisabledStates.set(button, button.disabled));
      }
      checkoutPending = pending;
      directForms.forEach((form) => {
        form.setAttribute('aria-busy', pending ? 'true' : 'false');
        form.querySelectorAll('button[type="submit"]').forEach((button) => {
          if (pending) button.disabled = true;
          else if (priorDisabledStates.has(button)) button.disabled = priorDisabledStates.get(button);
        });
      });
    };

    const setCheckoutStatus = (message = '', targetForm = null) => {
      directForms.forEach((form) => {
        const status = form.querySelector('[data-tv-checkout-status]');
        if (!status) return;
        const visibleMessage = message && (!targetForm || form === targetForm) ? message : '';
        status.textContent = visibleMessage;
        status.hidden = !visibleMessage;
      });
    };

    const requestJson = async (url, options = {}) => {
      const response = await fetch(url, options);
      if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
      return response.json();
    };

    const requireCart = (value) => {
      if (!value || !Array.isArray(value.items)) throw new Error('Invalid cart response');
      return value;
    };

    const propertiesEqual = (left = {}, right = {}) => {
      const leftEntries = Object.entries(left || {});
      const rightEntries = Object.entries(right || {});
      return leftEntries.length === rightEntries.length && rightEntries.every(
        ([key, value]) => Object.hasOwn(left || {}, key) && String(left[key]) === value
      );
    };

    const captureCheckoutSubmission = (form) => {
      const formData = new FormData(form);
      formData.delete('return_to');
      formData.set('quantity', '1');
      const properties = {};
      for (const [name, value] of formData.entries()) {
        if (
          name.startsWith('properties[')
          && name.endsWith(']')
          && typeof value === 'string'
          && value !== ''
        ) {
          properties[name.slice(11, -1)] = value;
        }
      }
      return {
        variantId: Number(formData.get('id')),
        formData,
        properties,
      };
    };

    const startDirectCheckout = async ({ variantId, formData, properties }) => {
      if (!Number.isSafeInteger(variantId) || variantId <= 0) {
        throw new Error('Missing product variant');
      }

      const routesRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const cart = requireCart(await requestJson(`${routesRoot}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }));

      const targetLines = cart.items.filter((item) => Number(item.variant_id) === variantId);
      if (targetLines.some((item) => typeof item.key !== 'string' || !item.key)) {
        throw new Error('Invalid cart line response');
      }

      const targetAlreadyNormalized = targetLines.length === 1
        && Number(targetLines[0].quantity) === 1
        && propertiesEqual(targetLines[0].properties, properties);

      if (targetAlreadyNormalized) {
        window.location.assign(`${routesRoot}checkout`);
        return;
      }

      const updates = Object.fromEntries(
        targetLines.map((item) => [item.key, 0])
      );

      if (Object.keys(updates).length) {
        const updatedCart = requireCart(await requestJson(`${routesRoot}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        }));
        if (updatedCart.items.some((item) => Number(item.variant_id) === variantId)) {
          throw new Error('Cart normalization failed');
        }
      }

      const addedItem = await requestJson(`${routesRoot}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const addedProperties = addedItem?.properties;
      const propertiesMatch = Object.entries(properties).every(
        ([key, value]) => addedProperties && String(addedProperties[key]) === value
      );
      if (
        !addedItem
        || typeof addedItem.key !== 'string'
        || !addedItem.key
        || Number(addedItem.variant_id) !== variantId
        || Number(addedItem.quantity) !== 1
        || !propertiesMatch
      ) {
        throw new Error('Invalid cart add response');
      }
      window.location.assign(`${routesRoot}checkout`);
    };

    const runDirectCheckout = (submission) => {
      if (navigator.locks?.request) {
        return navigator.locks.request(
          'truevitals-order-now-cart',
          () => startDirectCheckout(submission)
        );
      }
      return startDirectCheckout(submission);
    };

    directForms.forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (checkoutPending) return;
        const submission = captureCheckoutSubmission(form);
        setCheckoutPending(true);
        setCheckoutStatus();
        runDirectCheckout(submission).catch((error) => {
          console.error('TrueVitals direct checkout failed:', error);
          setCheckoutStatus("We couldn't start checkout. Please try again.", form);
          setCheckoutPending(false);
        });
      });
    });

    if (!sticky || !primaryForm) return;

    const themeRole = window.Shopify?.theme?.role;
    const queryPreview = new URLSearchParams(window.location.search).has('preview_theme_id');
    if (queryPreview || (themeRole && !['main', 'live'].includes(themeRole))) {
      root.classList.add('tv-pdp--preview');
    }

    const handlePageShow = (event) => {
      if (!event.persisted) return;
      setCheckoutPending(false);
      setCheckoutStatus();
    };
    window.addEventListener('pageshow', handlePageShow);

    let updatePending = false;
    const updateSticky = () => {
      const show = primaryForm.getBoundingClientRect().bottom <= 0;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      sticky.inert = !show;
      updatePending = false;
    };
    const requestStickyUpdate = () => {
      if (updatePending) return;
      updatePending = true;
      window.requestAnimationFrame(updateSticky);
    };

    sticky.inert = true;
    updateSticky();
    window.addEventListener('scroll', requestStickyUpdate, { passive: true });
    window.addEventListener('resize', requestStickyUpdate);
    root._tvOrganicCleanup = () => {
      window.removeEventListener('scroll', requestStickyUpdate);
      window.removeEventListener('resize', requestStickyUpdate);
      window.removeEventListener('pageshow', handlePageShow);
    };
  };

  const initWithin = (scope) => {
    if (scope.matches?.('[data-tv-organic-hero]')) initHero(scope);
    scope.querySelectorAll?.('[data-tv-organic-hero]').forEach(initHero);
  };

  window[namespace] = { initWithin };
  initWithin(document);
  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-tv-organic-hero]')
      ? event.target
      : event.target.querySelector?.('[data-tv-organic-hero]');
    root?._tvOrganicCleanup?.();
  });
})();
